import type { ReactNode } from "react";

export type FilterChipProps = {
  /** Visible label (string or node, e.g. label + count). */
  label: ReactNode;
  /** Whether the chip is currently selected. Parent owns the selection logic
   *  (single-select for the releases surface filter, multi-select for the stats
   *  session filters), so this component stays agnostic. */
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
};

/**
 * Shared filter pill used by both the statistics session filters (multi-select)
 * and the releases surface filter (single-select). This is the existing stats
 * pill look (rounded-full, gray-100 fill, brand-green active — green-500 is
 * #00c889 / green-600 #00b47b in tailwind.config) with one addition: an outline
 * (border) on the inactive state so it reads as clickable. A same-coloured
 * border on the active state keeps the box size stable when toggling.
 * Selection logic stays with the parent, so this component is agnostic to
 * single- vs multi-select.
 */
const FilterChip = ({
  label,
  active,
  onClick,
  disabled = false,
  className = "",
}: FilterChipProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-pressed={active}
    className={`inline-flex items-center px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors select-none disabled:opacity-50 disabled:cursor-not-allowed ${
      active
        ? "bg-green-500 text-white border-green-500 hover:bg-green-600"
        : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
    } ${className}`}
  >
    {label}
  </button>
);

export default FilterChip;
